import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { IonItemSliding, IonItem, IonItemOptions, IonItemOption } from '@ionic/react';
import { Icon } from '../../icons';
import { Badge, Btn, Card, MetricCard, DataTable, FormRow, Field, FileUpload, Modal, SearchBar, FormActions, Avatar } from '../../components/ui';
import * as documentApi from '../../api/document';
import { uploadFiles } from '../../api/files';
import { toast } from 'react-hot-toast';
import { ConfirmModal } from '../../components/shared';
import { formatDateByPreference } from '../../utils/formatters';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import en from '../../locales/en.json';
import hi from '../../locales/hi.json';
import gu from '../../locales/gu.json';

const translations = { en, hi, gu };

export default function DocumentsScreen() {
  const { user } = useAuth();
  const { language = 'en' } = useSettings();
  const t = (key, params) => {
    let str = translations[language]?.documents?.[key] || translations.en?.documents?.[key] || key;
    if (params) { Object.keys(params).forEach(p => { str = str.replace(`{${p}}`, params[p]); }); }
    return str;
  };

  const getModuleName = (m) => {
    if (!m) return "";
    const key = "mod_" + m.toLowerCase();
    return translations[language]?.documents?.[key] || translations.en?.documents?.[key] || m;
  };

  const isAdmin = user?.roles_list?.some(r => ['super_admin', 'admin'].includes(r));
  const isMember = (user?.primary_role_label === 'Member' || user?.roles_list?.includes('member')) && !isAdmin;

  const [modal, setModal] = useState(false);
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({ name: "", module: "Finance", description: "", version: "1.0", files: [] });
  const sf = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [docLimit, setDocLimit] = useState(-1);
  const [docCount, setDocCount] = useState(0);
  const [delItem, setDelItem] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [pubItem, setPubItem] = useState(null);

  // Sorting, Pagination, and Bulk Selection State
  const [sortField, setSortField] = useState('created_at'); // 'name' | 'file_size' | 'created_at' | 'version'
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' | 'desc'
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDelModal, setBulkDelModal] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Sharing & Access Modal State
  const [shareItem, setShareItem] = useState(null);
  const [shareForm, setShareForm] = useState({
    shared_with: 'all',
    permissions: { view: true, download: true, edit: false },
    expires_at: ''
  });
  const [savingShare, setSavingShare] = useState(false);

  const resetForm = () => {
    setForm({ name: "", module: "Finance", description: "", version: "1.0", files: [], shared_with: 'all', permissions: { view: true, download: true, edit: false }, expires_at: '' });
    setEditItem(null);
    setModal(false);
  };

  const modules = ["All", "Finance", "Vendors", "Notices", "Festival", "Maintenance"];

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== "All") params.module = filter;
      if (search) params.search = search;

      const res = await documentApi.getDocuments(params);
      setDocs(res.data || []);
      if (res.meta) {
        setDocLimit(res.meta.doc_limit ?? -1);
        setDocCount(res.meta.doc_count ?? 0);
      }
    } catch (err) {
      toast.error(t("toast_load_failed"));
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  const lastFetch = useRef(null);
  useEffect(() => {
    const current = JSON.stringify({ filter, search });
    if (lastFetch.current === current) return;
    lastFetch.current = current;
    fetchDocs();
  }, [fetchDocs, filter, search]);

  // Reset page & bulk selection on filter/search changes
  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds([]);
  }, [filter, search]);

  const save = async (status = "published") => {
    if (!form.name) return toast.error(t("val_name"));
    if (form.files.length === 0) return toast.error(t("val_file"));
    setSaving(true);
    try {
      let fileUrl = editItem ? editItem.file_url : null;
      let fileType = editItem ? editItem.file_type : null;
      let fileSize = editItem ? editItem.file_size : null;
      let fileIds = editItem ? editItem.file_ids : [];

      const newFiles = form.files.filter(f => f instanceof File);

      if (newFiles.length > 0) {
        const res = await uploadFiles(newFiles, 'documents');
        const newlyUploaded = res.data?.uploaded || res.uploaded || [];
        if (newlyUploaded.length > 0) {
          fileIds = newlyUploaded.map(f => f.id);
          fileUrl = newlyUploaded[0].file_url;
          const mime = newFiles[0].type.toLowerCase();
          if (mime.includes('pdf')) fileType = 'PDF';
          else if (mime.includes('spreadsheet') || mime.includes('excel') || mime.includes('csv')) fileType = 'Spreadsheet';
          else if (mime.includes('word') || mime.includes('document')) fileType = 'Doc';
          else if (mime.includes('image')) fileType = 'Image';
          else fileType = 'File';
          fileSize = (newFiles[0].size / 1024 / 1024).toFixed(1) + ' MB';
        }
      } else if (editItem && form.files.length === 0) {
        fileUrl = null;
        fileType = null;
        fileSize = null;
        fileIds = [];
      }

      const payload = {
        name: form.name,
        module: form.module,
        description: form.description,
        version: form.version,
        file_url: fileUrl,
        file_type: fileType,
        file_size: fileSize,
        file_ids: fileIds,
        shared_with: form.shared_with || 'all',
        permissions: form.permissions || { view: true, download: true, edit: false },
        expires_at: form.expires_at || null,
        status: status
      };

      if (editItem) {
        await documentApi.updateDocument(editItem.id, payload);
        toast.success(t("toast_updated"));
      } else {
        await documentApi.createDocument(payload);
        toast.success(t("toast_uploaded"));
      }

      resetForm();

      lastFetch.current = null; // force refetch
      fetchDocs();
    } catch (err) {
      console.error("Document save error:", err);
      const msg = err.message || err.response?.data?.message || (editItem ? t("toast_update_failed") : t("toast_upload_failed"));
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!delItem) return;
    try {
      await documentApi.deleteDocument(delItem.id);
      toast.success(t("toast_deleted"));
      setSelectedIds(prev => prev.filter(id => id !== delItem.id));
      setDelItem(null);
      lastFetch.current = null; // force refetch
      fetchDocs();
    } catch (err) {
      console.error("Delete document error:", err);
      const msg = err.message || err.response?.data?.message || t("toast_delete_failed");
      toast.error(msg);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setBulkDeleting(true);
    try {
      await documentApi.bulkDeleteDocuments(selectedIds);
      toast.success(t("toast_bulk_deleted", { count: selectedIds.length }));
      setSelectedIds([]);
      setBulkDelModal(false);
      lastFetch.current = null; // force refetch
      fetchDocs();
    } catch (err) {
      console.error("Bulk delete error:", err);
      toast.error(t("toast_bulk_delete_failed"));
    } finally {
      setBulkDeleting(false);
    }
  };

  const handlePublish = async () => {
    if (!pubItem) return;
    try {
      await documentApi.updateDocument(pubItem.id, { status: "published" });
      toast.success(t("toast_published"));
      setPubItem(null);
      lastFetch.current = null; // force refetch
      fetchDocs();
    } catch (err) {
      toast.error(t("toast_publish_failed"));
    }
  };

  const handleEdit = (d) => {
    setEditItem(d);
    setForm({
      name: d.name,
      module: d.module,
      description: d.description || "",
      version: d.version || "1.0",
      files: d.attachments || [],
      shared_with: d.shared_with || 'all',
      permissions: d.permissions || { view: true, download: true, edit: false },
      expires_at: d.expires_at ? new Date(d.expires_at).toISOString().split('T')[0] : ''
    });
    setModal(true);
  };

  const openShareModal = (d) => {
    setShareItem(d);
    setShareForm({
      shared_with: d.shared_with || 'all',
      permissions: d.permissions || { view: true, download: true, edit: false },
      expires_at: d.expires_at ? new Date(d.expires_at).toISOString().split('T')[0] : ''
    });
  };

  const saveSharing = async () => {
    if (!shareItem) return;
    setSavingShare(true);
    try {
      await documentApi.updateDocumentSharing(shareItem.id, {
        shared_with: shareForm.shared_with,
        permissions: shareForm.permissions,
        expires_at: shareForm.expires_at || null
      });
      toast.success(t("toast_sharing_updated"));
      setShareItem(null);
      lastFetch.current = null;
      fetchDocs();
    } catch (err) {
      console.error("Sharing update error:", err);
      toast.error(t("toast_sharing_failed"));
    } finally {
      setSavingShare(false);
    }
  };

  const copyShareLink = (doc) => {
    const origin = window.location.origin;
    const token = doc.share_token || doc.id;
    const url = `${origin}/shared-doc/${token}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
      toast.success(t("toast_link_copied"));
    } else {
      toast.success(`Link: ${url}`);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  // Helper for type-based thumbnail / icon styling
  const getFileTypeInfo = (doc) => {
    const type = (doc.file_type || '').toLowerCase();
    const name = (doc.name || '').toLowerCase();
    const url = (doc.file_url || '').toLowerCase();

    if (type.includes('pdf') || name.endsWith('.pdf') || url.includes('.pdf')) {
      return { icon: 'file_text', color: 'text-red-500', bg: 'bg-red-50 dark:bg-red-950/40', border: 'border-red-200 dark:border-red-800', label: 'PDF', badgeColor: 'red' };
    }
    if (type.includes('image') || name.match(/\.(jpg|jpeg|png|gif|webp|svg)$/) || url.match(/\.(jpg|jpeg|png|gif|webp|svg)/)) {
      return { icon: 'file_image', color: 'text-emerald-500', bg: 'bg-emerald-50 dark:bg-emerald-950/40', border: 'border-emerald-200 dark:border-emerald-800', label: 'Image', badgeColor: 'green' };
    }
    if (type.includes('sheet') || type.includes('excel') || type.includes('csv') || name.match(/\.(xls|xlsx|csv)$/)) {
      return { icon: 'file_spreadsheet', color: 'text-teal-600', bg: 'bg-teal-50 dark:bg-teal-950/40', border: 'border-teal-200 dark:border-teal-800', label: 'Excel', badgeColor: 'teal' };
    }
    return { icon: 'file', color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-950/40', border: 'border-blue-200 dark:border-blue-800', label: doc.file_type || 'Doc', badgeColor: 'blue' };
  };

  // Parse size helper for reliable sorting and calculation
  const parseSizeInBytes = (sizeStr) => {
    if (!sizeStr) return 0;
    if (typeof sizeStr === 'number') return sizeStr;
    const str = String(sizeStr).trim().toUpperCase();
    const val = parseFloat(str.replace(/[^0-9.]/g, '')) || 0;
    if (str.includes('GB')) return val * 1024 * 1024 * 1024;
    if (str.includes('MB')) return val * 1024 * 1024;
    if (str.includes('KB')) return val * 1024;
    if (str.includes('B')) return val;
    return val > 10000 ? val : val * 1024 * 1024; // default assume bytes if large, else MB
  };

  const formatStorageSize = (bytes) => {
    if (!bytes || bytes <= 0) return '0 MB';
    const mb = bytes / (1024 * 1024);
    if (mb >= 1024) {
      const gb = mb / 1024;
      return `${parseFloat(gb.toFixed(1))} GB`;
    }
    if (mb >= 1) {
      return `${parseFloat(mb.toFixed(1))} MB`;
    }
    const kb = bytes / 1024;
    if (kb >= 1) {
      return `${parseFloat(kb.toFixed(1))} KB`;
    }
    return `${Math.round(bytes)} B`;
  };

  // Sort documents
  const sortedDocs = [...docs].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];

    if (sortField === 'name') {
      aVal = (a.name || '').toLowerCase();
      bVal = (b.name || '').toLowerCase();
      return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    if (sortField === 'file_size') {
      aVal = parseSizeInBytes(a.file_size);
      bVal = parseSizeInBytes(b.file_size);
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    }
    if (sortField === 'version') {
      aVal = parseFloat(String(a.version || '0').replace(/[^0-9.]/g, '')) || 0;
      bVal = parseFloat(String(b.version || '0').replace(/[^0-9.]/g, '')) || 0;
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    }
    if (sortField === 'created_at') {
      aVal = new Date(a.created_at || 0).getTime();
      bVal = new Date(b.created_at || 0).getTime();
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    }
    return 0;
  });

  // Pagination calculation
  const totalPages = Math.ceil(sortedDocs.length / pageSize) || 1;
  const paginatedDocs = sortedDocs.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Bulk selection helpers
  const allCurrentPageSelected = paginatedDocs.length > 0 && paginatedDocs.every(d => selectedIds.includes(d.id));
  const someCurrentPageSelected = paginatedDocs.some(d => selectedIds.includes(d.id)) && !allCurrentPageSelected;

  const toggleSelectAllCurrentPage = () => {
    if (allCurrentPageSelected) {
      const pageIds = paginatedDocs.map(d => d.id);
      setSelectedIds(prev => prev.filter(id => !pageIds.includes(id)));
    } else {
      const pageIds = paginatedDocs.map(d => d.id);
      setSelectedIds(prev => [...new Set([...prev, ...pageIds])]);
    }
  };

  const toggleSelectDoc = (id) => {
    setSelectedIds(prev => (
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    ));
  };

  const totalStorageBytes = docs.reduce((acc, d) => {
    return acc + parseSizeInBytes(d.file_size);
  }, 0);
  const totalStorage = formatStorageSize(totalStorageBytes);

  if (isMember) {
    return (
      <div className="fade-up flex items-center justify-center h-full min-h-[400px]">
        <div className="text-center space-y-3 max-w-sm">
          <div className="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
             <Icon name="shield" size={32} className="text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-ink dark:text-white">{t("title_restricted")}</h2>
          <p className="text-muted dark:text-slate-400">{t("msg_restricted")}</p>
        </div>
      </div>
    );
  }

  const renderSortableHeader = (label, field) => (
    <button
      type="button"
      onClick={() => handleSort(field)}
      className="inline-flex items-center gap-1.5 font-bold uppercase tracking-wider text-[11px] text-muted hover:text-ink transition-colors cursor-pointer border-0 bg-transparent p-0"
    >
      <span>{label}</span>
      <span className="flex flex-col text-[8px] leading-none opacity-70">
        {sortField === field ? (
          sortDirection === 'asc' ? <Icon name="arrow_up" size={10} className="text-primary" /> : <Icon name="arrow_down" size={10} className="text-primary" />
        ) : (
          <span className="text-slate-400">⇅</span>
        )}
      </span>
    </button>
  );

  return (
    <div className="fade-up space-y-4">
      {/* Upload / Edit Document Modal */}
      {modal && (
        <Modal 
          title={editItem ? t("title_edit_doc") : t("title_upload_doc")} 
          onClose={resetForm}
          footer={
            <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 w-full">
              <Btn onClick={resetForm} variant="default" className="text-muted hover:text-ink justify-center w-full sm:w-auto">
                {t("btn_cancel")}
              </Btn>
              <div className="flex gap-2 flex-1 sm:flex-initial">
                <Btn icon="save" onClick={() => save("draft")} variant="default" disabled={saving} className="flex-1 sm:flex-initial justify-center">
                  {t("btn_save_draft")}
                </Btn>
                <Btn icon="check" variant="primary" onClick={() => save("published")} loading={saving} className="flex-1 sm:flex-initial justify-center">
                  {t("btn_publish")}
                </Btn>
              </div>
            </div>
          }
        >
          {/* Supported formats & max size hint */}
          <div className="mb-4 p-3 rounded-xl bg-blue-50/80 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/60 text-xs flex items-start gap-2.5">
            <Icon name="info" size={16} className="text-blue-500 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-blue-900 dark:text-blue-200">{t("upload_hints_title")}</div>
              <div className="text-blue-700 dark:text-blue-300/80 mt-0.5 leading-relaxed">{t("upload_hints_desc")}</div>
            </div>
          </div>

          <FormRow><Field label={t("label_doc_name")} value={form.name} onChange={sf("name")} required /></FormRow>
          <FormRow cols={2}>
            <Field label={t("label_module")} type="select" value={form.module} onChange={sf("module")} options={[{value:"Finance",label:t("mod_finance")}, {value:"Vendors",label:t("mod_vendors")}, {value:"Notices",label:t("mod_notices")}, {value:"Quotations",label:t("mod_quotations")}, {value:"Festival",label:t("mod_festival")}, {value:"Maintenance",label:t("mod_maintenance")}, {value:"General",label:t("mod_general")}]} />
            <Field label={t("label_version")} value={form.version} onChange={sf("version")} />
          </FormRow>

          <FormRow cols={2}>
            <Field
              label={t("label_shared_with")}
              type="select"
              value={form.shared_with || 'all'}
              onChange={sf("shared_with")}
              options={[
                { value: "all", label: t("opt_share_all") },
                { value: "committee", label: t("opt_share_committee") },
                { value: "residents", label: t("opt_share_residents") },
                { value: "private", label: t("opt_share_private") }
              ]}
            />
            <Field
              label={t("label_expires_at")}
              type="date"
              value={form.expires_at || ''}
              onChange={sf("expires_at")}
            />
          </FormRow>

          <FormRow><Field label={t("label_desc")} type="textarea" value={form.description} onChange={sf("description")} rows={2} /></FormRow>
          <FileUpload label={editItem ? t("label_doc_file") : t("label_upload_file")} accept="image/*,.pdf,.xlsx,.csv,.doc,.docx" files={form.files} onChange={files => setForm(f => ({ ...f, files }))} required />
        </Modal>
      )}

      {/* Share & Permissions Modal */}
      {shareItem && (
        <Modal 
          title={t("title_share_modal")} 
          onClose={() => setShareItem(null)}
          footer={
            <div className="flex items-center justify-end gap-2 w-full">
              <Btn onClick={() => setShareItem(null)} variant="default" className="flex-1 sm:flex-initial justify-center">{t("btn_cancel")}</Btn>
              <Btn variant="primary" icon="check" loading={savingShare} onClick={saveSharing} className="flex-1 sm:flex-initial justify-center">
                {t("btn_save_sharing")}
              </Btn>
            </div>
          }
        >
          <div className="space-y-4 text-xs">
            {/* Header info */}
            <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <div>
                <div className="font-bold text-sm text-ink dark:text-white">{shareItem.name}</div>
                <div className="text-muted mt-0.5">{getModuleName(shareItem.module)} • v{shareItem.version} • {shareItem.file_size || '-'}</div>
              </div>
              <Badge color={shareItem.is_expired ? 'red' : 'green'} sm>
                {shareItem.is_expired ? t("status_expired") : t("status_shared")}
              </Badge>
            </div>

            {/* Target Audience */}
            <FormRow>
              <Field
                label={t("label_shared_with")}
                type="select"
                value={shareForm.shared_with}
                onChange={e => setShareForm(f => ({ ...f, shared_with: e.target.value }))}
                options={[
                  { value: "all", label: t("opt_share_all") },
                  { value: "committee", label: t("opt_share_committee") },
                  { value: "residents", label: t("opt_share_residents") },
                  { value: "private", label: t("opt_share_private") }
                ]}
              />
            </FormRow>

            {/* Permissions View / Download / Edit Checkboxes */}
            <div className="space-y-2">
              <label className="font-semibold text-slate-700 dark:text-slate-200 block">{t("label_permissions")}</label>
              <div className="grid grid-cols-3 gap-2">
                <label className="flex items-center gap-2 p-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={shareForm.permissions?.view ?? true}
                    onChange={e => setShareForm(f => ({ ...f, permissions: { ...f.permissions, view: e.target.checked } }))}
                    className="w-4 h-4 rounded text-primary focus:ring-primary"
                  />
                  <span className="font-medium text-slate-800 dark:text-slate-200">{t("perm_view")}</span>
                </label>
                <label className="flex items-center gap-2 p-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={shareForm.permissions?.download ?? true}
                    onChange={e => setShareForm(f => ({ ...f, permissions: { ...f.permissions, download: e.target.checked } }))}
                    className="w-4 h-4 rounded text-primary focus:ring-primary"
                  />
                  <span className="font-medium text-slate-800 dark:text-slate-200">{t("perm_download")}</span>
                </label>
                <label className="flex items-center gap-2 p-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={shareForm.permissions?.edit ?? false}
                    onChange={e => setShareForm(f => ({ ...f, permissions: { ...f.permissions, edit: e.target.checked } }))}
                    className="w-4 h-4 rounded text-primary focus:ring-primary"
                  />
                  <span className="font-medium text-slate-800 dark:text-slate-200">{t("perm_edit")}</span>
                </label>
              </div>
            </div>

            {/* Optional Expiry Date */}
            <FormRow>
              <Field
                label={t("label_expires_at")}
                type="date"
                value={shareForm.expires_at}
                onChange={e => setShareForm(f => ({ ...f, expires_at: e.target.value }))}
              />
            </FormRow>

            {/* Share Link Generation & Copy Button */}
            <div className="space-y-1.5 pt-2">
              <label className="font-semibold text-slate-700 dark:text-slate-200 block">{t("label_share_link")}</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={`${window.location.origin}/shared-doc/${shareItem.share_token || shareItem.id}`}
                  className="flex-1 p-2 text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 font-mono text-slate-700 dark:text-slate-300"
                />
                <Btn
                  variant="secondary"
                  sm
                  icon="content_copy"
                  onClick={() => copyShareLink(shareItem)}
                >
                  {t("btn_copy_link")}
                </Btn>
              </div>
            </div>
          </div>
        </Modal>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard label={t("card_total_docs")} value={docs.length} icon="documents" accent="blue" />
        <MetricCard label={t("card_pdf_files")} value={docs.filter(d => (d.file_type || '').includes("PDF") || (d.name || '').endsWith('.pdf')).length} icon="file_text" accent="red" />
        <MetricCard label={t("card_images")} value={docs.filter(d => (d.file_type || '').includes("Image") || (d.name || '').match(/\.(jpg|jpeg|png|webp|gif)$/i)).length} icon="file_image" accent="green" />
        <MetricCard label={t("card_total_storage")} value={totalStorage} icon="activity" accent="amber" />
      </div>
      <Card>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 mb-4">
          <SearchBar value={search} onChange={setSearch} placeholder={t("placeholder_search")} className="w-full sm:w-56" />
          <div className="flex gap-1 overflow-x-auto whitespace-nowrap pb-1 scrollbar-hide">
            {modules.map(m => (
              <button key={m} onClick={() => setFilter(m)} className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer border-0 transition-all ${filter === m ? "bg-primary text-white" : "bg-slate-100 dark:bg-slate-700 text-muted dark:text-slate-300 hover:text-ink"}`}>{getModuleName(m)}</button>
            ))}
          </div>
          
          <div className={`items-center gap-2 ml-auto ${selectedIds.length > 0 ? 'flex' : 'hidden lg:flex'}`}>
            {selectedIds.length > 0 && (
              <Btn
                sm
                variant="solid_danger"
                icon="trash"
                onClick={() => setBulkDelModal(true)}
              >
                {t("btn_bulk_delete", { count: selectedIds.length })}
              </Btn>
            )}
            {docLimit !== -1 && docCount >= docLimit ? (
              <Btn sm variant="primary" icon="upload" className="hidden lg:inline-flex opacity-50 grayscale" onClick={() => toast.error(`Storage limit reached (${docCount}/${docLimit}). Please upgrade your plan.`)}>{t("btn_upload")}</Btn>
            ) : (
              <Btn sm variant="primary" icon="upload" className="hidden lg:inline-flex" onClick={() => { resetForm(); setModal(true); }}>{t("btn_upload")}</Btn>
            )}
          </div>
        </div>

        {loading ? (
          <div className="animate-pulse flex flex-col gap-3 mt-4">
            {[1, 2, 3].map(i => <div key={i} className="h-12 bg-slate-100 dark:bg-slate-700 rounded-xl w-full"></div>)}
          </div>
        ) : (
          <>
            <div className="hidden lg:block">
              <DataTable
                horizontalScroll={false}
                cols={[
                  <div className="flex items-center gap-2.5">
                    <label className="relative flex items-center justify-center cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={allCurrentPageSelected}
                        ref={el => { if (el) el.indeterminate = someCurrentPageSelected; }}
                        onChange={toggleSelectAllCurrentPage}
                        className="sr-only peer"
                      />
                      <div className={`w-4 h-4 rounded border transition-all flex items-center justify-center ${
                        allCurrentPageSelected 
                          ? 'bg-primary border-primary shadow-xs' 
                          : someCurrentPageSelected 
                            ? 'bg-primary/90 border-primary' 
                            : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800/80 group-hover:border-primary/60'
                      }`}>
                        {allCurrentPageSelected && (
                          <svg className="w-3 h-3 text-white stroke-[3]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                        {someCurrentPageSelected && (
                          <div className="w-2 h-0.5 bg-white rounded-full" />
                        )}
                      </div>
                    </label>
                    {renderSortableHeader(t("col_document"), 'name')}
                  </div>,
                  t("col_module"),
                  t("col_type"),
                  renderSortableHeader(t("col_size"), 'file_size'),
                  renderSortableHeader(t("col_version"), 'version'),
                  renderSortableHeader(t("col_uploaded"), 'created_at'),
                  t("col_by"),
                  t("col_actions")
                ]}
                rows={paginatedDocs.map(d => {
                  const uploadedDate = formatDateByPreference(d.created_at);
                  const uploaderName = d.uploader?.name || "System";
                  const firstName = uploaderName.split(" ")[0];
                  const typeInfo = getFileTypeInfo(d);
                  const isSelected = selectedIds.includes(d.id);
                  const isExpired = d.is_expired || (d.expires_at && new Date(d.expires_at) < new Date());

                  return [
                    <div className="flex items-center gap-3 min-w-[200px]">
                      <label className="relative flex items-center justify-center cursor-pointer group shrink-0">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelectDoc(d.id)}
                          className="sr-only peer"
                        />
                        <div className={`w-4 h-4 rounded border transition-all flex items-center justify-center ${
                          isSelected 
                            ? 'bg-primary border-primary shadow-xs ring-2 ring-primary/20' 
                            : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800/80 group-hover:border-primary/60'
                        }`}>
                          {isSelected && (
                            <svg className="w-3 h-3 text-white stroke-[3]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </div>
                      </label>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center border shrink-0 ${typeInfo.bg} ${typeInfo.border}`}>
                        <Icon name={typeInfo.icon} size={15} className={typeInfo.color} />
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="font-medium text-ink text-sm truncate">{d.name}</span>
                        {isExpired && (
                          <span className="text-[10px] text-red-500 font-semibold">{t("status_expired")}</span>
                        )}
                      </div>
                    </div>,
                    <div className="flex flex-col gap-1 items-start">
                      <Badge color="blue">{getModuleName(d.module)}</Badge>
                      {d.status === "draft" && <Badge color="gray" dot>{t("status_draft")}</Badge>}
                      {d.shared_with && d.shared_with !== 'all' && (
                        <span className="text-[10px] text-indigo-600 dark:text-indigo-400 capitalize font-medium">
                          {d.shared_with}
                        </span>
                      )}
                    </div>,
                    <Badge color={typeInfo.badgeColor}>{typeInfo.label}</Badge>,
                    d.file_size || "-", `v${d.version}`, uploadedDate,
                    <div className="flex items-center gap-1.5"><Avatar name={uploaderName} size={18} /><span className="text-xs text-muted">{firstName}</span></div>,
                    <div className="flex items-center gap-3.5 whitespace-nowrap">
                      <div className="flex items-center gap-3 rounded-lg p-0.5">
                        <Btn xs variant="ghost" icon="eye" onClick={() => {
                          if (isExpired) {
                            toast.error(t("msg_doc_expired"));
                            return;
                          }
                          if (d.file_url) window.open(d.file_url, '_blank');
                          else toast.error(t("toast_no_file"));
                        }}>{t("btn_view")}</Btn>
                        <Btn xs variant="ghost" icon="download" onClick={() => {
                          if (isExpired) {
                            toast.error(t("msg_doc_expired"));
                            return;
                          }
                          const dUrl = d.download_url || d.file_url;
                          if (dUrl) window.location.href = dUrl;
                          else toast.error(t("toast_no_file"));
                        }}>{t("btn_dl")}</Btn>
                        <Btn xs variant="ghost" icon="link" onClick={() => openShareModal(d)}>
                          {t("btn_share")}
                        </Btn>
                      </div>
                      {d.status === "draft" && !d.is_system_file && <Btn xs icon="check" variant="primary" onClick={() => setPubItem(d)}>{t("btn_publish")}</Btn>}
                      {!d.is_system_file && <Btn xs icon="edit" onClick={() => handleEdit(d)}>{t("btn_edit")}</Btn>}
                      {!d.is_system_file && <Btn xs variant="solid_danger" icon="trash" onClick={() => setDelItem(d)}>{t("btn_delete")}</Btn>}
                    </div>
                  ];
                })}
                emptyMsg={t("msg_no_docs")}
              />
            </div>

            {/* Mobile View */}
            <div className="block lg:hidden space-y-3 mt-3">
              {paginatedDocs.length === 0 && <p className="text-sm text-muted text-center py-8">{t("msg_no_docs")}</p>}
              {paginatedDocs.map((d, i) => {
                const typeInfo = getFileTypeInfo(d);
                const isSelected = selectedIds.includes(d.id);
                const isExpired = d.is_expired || (d.expires_at && new Date(d.expires_at) < new Date());

                return (
                  <IonItemSliding key={d.id} className={`rounded-xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden ${i === 0 ? 'animate-swipe-hint' : ''}`} style={{ '--background': 'transparent' }}>
                    <IonItem lines="none" style={{ '--background': 'transparent', '--padding-start': '0', '--inner-padding-end': '0' }}>
                      <div className="flex w-full items-start gap-3 p-3 bg-white dark:bg-slate-900">
                        <label className="relative flex items-center justify-center cursor-pointer group mt-3 shrink-0">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelectDoc(d.id)}
                            className="sr-only peer"
                          />
                          <div className={`w-4 h-4 rounded border transition-all flex items-center justify-center ${
                            isSelected 
                              ? 'bg-primary border-primary shadow-xs ring-2 ring-primary/20' 
                              : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800/80 group-hover:border-primary/60'
                          }`}>
                            {isSelected && (
                              <svg className="w-3 h-3 text-white stroke-[3]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                          </div>
                        </label>
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 border ${typeInfo.bg} ${typeInfo.border}`}>
                          <Icon name={typeInfo.icon} size={18} className={typeInfo.color} />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-bold text-ink truncate mb-1">{d.name}</div>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <Badge color="blue" sm>{getModuleName(d.module)}</Badge>
                            {d.status === "draft" && <Badge color="gray" dot sm>{t("status_draft")}</Badge>}
                            {isExpired && <Badge color="red" sm>{t("status_expired")}</Badge>}
                            <span className="text-[10px] text-muted">v{d.version}</span>
                            <span className="text-[10px] text-muted">{d.file_size || "-"}</span>
                          </div>
                          <div className="text-[10px] text-muted mt-1.5 flex items-center gap-1.5">
                            <Avatar name={d.uploader?.name || "System"} size={14} />
                            <span>{d.uploader?.name?.split(" ")[0] || "System"} • {formatDateByPreference(d.created_at)}</span>
                          </div>
                        </div>

                        <div className="text-slate-300 flex flex-col items-center justify-center pt-3 gap-2 ml-1">
                          <button type="button" onClick={(e) => { e.stopPropagation(); if (isExpired) { toast.error(t("msg_doc_expired")); return; } if (d.file_url) window.open(d.file_url, '_blank'); else toast.error(t("toast_no_file")); }} className="w-8 h-8 flex items-center justify-center rounded-full bg-primary/10 text-primary cursor-pointer">
                            <Icon name="eye" size={14} />
                          </button>
                          <Icon name="chevron_left" size={14} />
                        </div>
                      </div>
                    </IonItem>
                    
                    <IonItemOptions side="end">
                      <IonItemOption color="secondary" onClick={() => { if (isExpired) { toast.error(t("msg_doc_expired")); return; } const dUrl = d.download_url || d.file_url; if (dUrl) window.location.href = dUrl; else toast.error(t("toast_no_file")); }}>
                        <div className="flex flex-col items-center justify-center w-12">
                          <Icon name="download" size={20} color="white" />
                        </div>
                      </IonItemOption>
                      {!d.is_system_file && (
                        <IonItemOption color="tertiary" onClick={() => openShareModal(d)}>
                          <div className="flex flex-col items-center justify-center w-12">
                            <Icon name="link" size={20} color="white" />
                          </div>
                        </IonItemOption>
                      )}
                      {d.status === "draft" && !d.is_system_file && (
                        <IonItemOption color="success" onClick={() => setPubItem(d)}>
                          <div className="flex flex-col items-center justify-center w-12">
                            <Icon name="check" size={20} color="white" />
                          </div>
                        </IonItemOption>
                      )}
                      {!d.is_system_file && (
                        <IonItemOption color="primary" onClick={() => handleEdit(d)}>
                          <div className="flex flex-col items-center justify-center w-12">
                            <Icon name="edit" size={20} color="white" />
                          </div>
                        </IonItemOption>
                      )}
                      {!d.is_system_file && (
                        <IonItemOption color="danger" onClick={() => setDelItem(d)}>
                          <div className="flex flex-col items-center justify-center w-12">
                            <Icon name="trash" size={20} color="white" />
                          </div>
                        </IonItemOption>
                      )}
                    </IonItemOptions>
                  </IonItemSliding>
                );
              })}
            </div>

            {/* Pagination Controls */}
            {sortedDocs.length > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 mt-4 border-t border-slate-100 dark:border-slate-800 text-xs text-slate-500 dark:text-slate-400">
                <div>
                  {t("showing_entries", {
                    from: Math.min((currentPage - 1) * pageSize + 1, sortedDocs.length),
                    to: Math.min(currentPage * pageSize, sortedDocs.length),
                    total: sortedDocs.length
                  })}
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center gap-1.5">
                    <Btn
                      xs
                      variant="secondary"
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    >
                      {t("btn_previous")}
                    </Btn>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, idx) => idx + 1)
                        .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                        .map((p, idx, arr) => {
                          const prev = arr[idx - 1];
                          return (
                            <span key={p} className="flex items-center">
                              {prev && p - prev > 1 && <span className="px-1 text-slate-400">…</span>}
                              <button
                                type="button"
                                onClick={() => setCurrentPage(p)}
                                className={`w-6 h-6 rounded flex items-center justify-center text-xs font-semibold cursor-pointer border-0 transition-colors ${
                                  currentPage === p
                                    ? 'bg-primary text-white'
                                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                                }`}
                              >
                                {p}
                              </button>
                            </span>
                          );
                        })}
                    </div>
                    <Btn
                      xs
                      variant="secondary"
                      disabled={currentPage >= totalPages}
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    >
                      {t("btn_next")}
                    </Btn>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Card>

      {delItem && (
        <ConfirmModal
          title={t("title_delete_doc")}
          text={t("msg_delete_doc", { name: delItem.name })}
          onConfirm={handleDelete}
          onCancel={() => setDelItem(null)}
          confirmText={t("btn_delete")}
        />
      )}
      {bulkDelModal && (
        <ConfirmModal
          title={t("title_bulk_delete")}
          text={t("msg_bulk_delete", { count: selectedIds.length })}
          onConfirm={handleBulkDelete}
          onCancel={() => setBulkDelModal(false)}
          confirmText={t("btn_delete")}
        />
      )}
      {pubItem && (
        <ConfirmModal
          title={t("title_publish_doc")}
          text={t("msg_publish_doc", { name: pubItem.name })}
          onConfirm={handlePublish}
          onCancel={() => setPubItem(null)}
          confirmText={t("btn_publish")}
        />
      )}
      
      {/* FAB for Mobile */}
      {!modal && !shareItem && !pubItem && !delItem && !bulkDelModal && createPortal(
        <div className="fixed bottom-6 right-6 z-50 lg:hidden pointer-events-auto">
          <button
            onClick={() => {
              if (docLimit !== -1 && docCount >= docLimit) {
                toast.error(`Storage limit reached (${docCount}/${docLimit}). Please upgrade your plan.`);
              } else {
                resetForm();
                setModal(true);
              }
            }}
            className="w-14 h-14 bg-primary text-white rounded-full flex items-center justify-center shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:scale-105 transition-transform active:scale-95 border-0 cursor-pointer"
          >
            <Icon name="plus" size={24} />
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}
